import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  LoaderButtonComponent,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { MultiFactorResolver, PhoneMultiFactorInfo } from 'firebase/auth';
import { AuthService } from '../../auth.service';
import { getAuthErrorMessage } from '../auth-error-messages';
import { MultifactorRecaptchaService } from '../multifactor-recaptcha.service';
import { MultifactorService } from '../multifactor.service';

interface IVerifyForm {
  code: string;
}

@Component({
  selector: 'pt-phone-verify',
  templateUrl: './phone-verify.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    LoaderButtonComponent,
  ],
})
export class PhoneVerifyComponent {
  private _multifactorService = inject(MultifactorService);
  private _recaptchaService = inject(MultifactorRecaptchaService);

  auth = inject(AuthService);
  resolver = input.required<MultiFactorResolver>();
  hint = input.required<PhoneMultiFactorInfo>();
  verified = output<void>();
  verificationId: string | undefined;
  isLoading = signal(false);
  errorMessage = '';

  codeForm = new TypedFormGroup<IVerifyForm>({
    code: new TypedFormControl<string>('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  async sendCode(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      const verifier = this._recaptchaService.initRecaptcha(
        'recaptcha-container-verify'
      );
      this.verificationId = await this._multifactorService.verifyWithPhone(
        this.resolver(),
        this.hint(),
        verifier
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA phone verify send code failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
      this._recaptchaService.clearRecaptcha();
    } finally {
      this.isLoading.set(false);
    }
  }

  async verify(): Promise<void> {
    if (!this.codeForm.valid || !this.verificationId) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage = '';
    try {
      await this._multifactorService.completePhoneVerification(
        this.resolver(),
        this.verificationId,
        this.codeForm.value.code
      );
      this._recaptchaService.clearRecaptcha();
      this.verified.emit();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA phone verify failed:', error);
      this.errorMessage = getAuthErrorMessage(error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
